import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { useCategories } from '../context/CategoriesContext'
import { useLanguage } from '../context/LanguageContext'
import { getDateRange, formatDate } from '../lib/utils'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Switch } from '../components/ui/switch'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog'
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Cell 
} from 'recharts'
import { TrendingUp, TrendingDown, Target, Calendar, Archive, ArrowLeft, Layers, Settings } from 'lucide-react'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

export default function ProgresoPage() {
  const { t, locale } = useLanguage()
  const { activeCategories, archivedCategories, fetchCategories } = useCategories()
  const [dateRange, setDateRange] = useState(() => getDateRange(7))
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedCategories, setSelectedCategories] = useState([])
  const [showByCategory, setShowByCategory] = useState(false)
  const [includeArchived, setIncludeArchived] = useState(false)
  
  // Drill-down state
  const [drillDownCategory, setDrillDownCategory] = useState(null)
  const [drillDownStats, setDrillDownStats] = useState(null)
  const [drillDownLogs, setDrillDownLogs] = useState([])
  const [loadingDrillDown, setLoadingDrillDown] = useState(false)
  
  const quickRanges = [
    { label: locale === 'es-MX' ? '7 días' : '7 days', days: 7 },
    { label: locale === 'es-MX' ? '14 días' : '14 days', days: 14 },
    { label: locale === 'es-MX' ? '30 días' : '30 days', days: 30 },
    { label: locale === 'es-MX' ? '3 meses' : '3 months', days: 90 },
  ]

  const allCategories = includeArchived 
    ? [...activeCategories, ...archivedCategories]
    : activeCategories

  const fetchStats = useCallback(async () => {
    setLoading(true)
    try {
      const params = {
        start_date: dateRange.start,
        end_date: dateRange.end,
        include_archived: includeArchived
      }
      if (selectedCategories.length > 0) {
        params.category_ids = selectedCategories.join(',')
      }
      const response = await axios.get(`${API}/logs/stats`, { params })
      setStats(response.data)
    } catch (error) {
      console.error('Error fetching stats:', error)
    } finally {
      setLoading(false)
    }
  }, [dateRange, selectedCategories, includeArchived])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  useEffect(() => {
    if (includeArchived) {
      fetchCategories(true)
    }
  }, [includeArchived, fetchCategories])

  // Fetch drill-down data for a category
  const fetchDrillDown = useCallback(async (category) => {
    setLoadingDrillDown(true)
    setDrillDownCategory(category)
    try {
      // Fetch stats with subcategory breakdown
      const statsResponse = await axios.get(`${API}/logs/stats`, {
        params: {
          start_date: dateRange.start,
          end_date: dateRange.end,
          category_id: category.category_id
        }
      })
      setDrillDownStats(statsResponse.data)
      
      // Fetch recent logs for this category
      const logsResponse = await axios.get(`${API}/logs`, {
        params: {
          category_id: category.category_id,
          start_date: dateRange.start,
          end_date: dateRange.end
        }
      })
      setDrillDownLogs(logsResponse.data.slice(0, 20))
    } catch (error) {
      console.error('Error fetching drill-down:', error)
    } finally {
      setLoadingDrillDown(false)
    }
  }, [dateRange])

  const closeDrillDown = () => {
    setDrillDownCategory(null)
    setDrillDownStats(null)
    setDrillDownLogs([])
  }

  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    )
  }

  const chartData = stats?.daily_breakdown?.map(d => ({
    date: formatDate(d.date).split(',')[0],
    fullDate: d.date,
    points: d.points
  })) || []

  const categoryChartData = stats?.category_breakdown?.map(d => ({
    name: d.category,
    category_id: d.category_id,
    points: d.points,
    color: d.color || '#6b7280'
  })) || []

  const subcategoryChartData = drillDownStats?.subcategory_breakdown?.map(d => ({
    name: d.subcategory,
    subcategory_id: d.subcategory_id,
    points: d.points
  })) || []

  // Handle bar click for drill-down
  const handleBarClick = (data) => {
    if (data && data.category_id) {
      fetchDrillDown(data)
    }
  }

  return (
    <div className="p-4 md:p-8 pb-safe max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold mb-1">{locale === 'es-MX' ? 'Evolución' : 'Progress'}</h1>
        <p className="text-muted-foreground text-sm">
          {locale === 'es-MX' ? 'Analiza tu crecimiento por rangos de fechas' : 'Analyze your growth over date ranges'}
        </p>
      </div>

      {/* Date Range Selector */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {locale === 'es-MX' ? 'Rango de fechas' : 'Date range'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            {quickRanges.map((range) => (
              <Button
                key={range.days}
                variant="outline"
                size="sm"
                className="rounded-full"
                onClick={() => setDateRange(getDateRange(range.days))}
                data-testid={`range-${range.days}`}
              >
                {range.label}
              </Button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs">{locale === 'es-MX' ? 'Desde' : 'From'}</Label>
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="w-full"
                data-testid="date-start"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">{locale === 'es-MX' ? 'Hasta' : 'To'}</Label>
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="w-full"
                data-testid="date-end"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Category Filters */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-medium">{locale === 'es-MX' ? 'Filtrar categorías' : 'Filter categories'}</CardTitle>
            {archivedCategories.length > 0 && (
              <div className="flex items-center gap-2">
                <Archive className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">{locale === 'es-MX' ? 'Mostrar archivadas' : 'Show archived'}</span>
                <Switch
                  checked={includeArchived}
                  onCheckedChange={setIncludeArchived}
                  data-testid="include-archived-toggle"
                />
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {allCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => toggleCategory(cat.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  selectedCategories.includes(cat.id)
                    ? 'text-white'
                    : 'bg-secondary text-foreground hover:bg-secondary/80'
                } ${!cat.is_active ? 'opacity-60' : ''}`}
                style={selectedCategories.includes(cat.id) ? { backgroundColor: cat.color } : {}}
                data-testid={`filter-${cat.id}`}
              >
                {cat.name}
                {!cat.is_active && ' (archivada)'}
              </button>
            ))}
            {selectedCategories.length > 0 && (
              <button
                onClick={() => setSelectedCategories([])}
                className="px-3 py-1.5 rounded-full text-sm font-medium text-muted-foreground hover:text-foreground"
              >
                {locale === 'es-MX' ? 'Limpiar' : 'Clear'}
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="stat-card" data-testid="stat-total">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{locale === 'es-MX' ? 'Total puntos' : 'Total points'}</span>
            </div>
            <p className="text-2xl font-bold">{stats.total_points}</p>
          </div>
          
          <div className="stat-card" data-testid="stat-avg">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{locale === 'es-MX' ? 'Promedio/día' : 'Avg/day'}</span>
            </div>
            <p className="text-2xl font-bold">{stats.avg_per_day}</p>
          </div>
          
          <div className="stat-card" data-testid="stat-best">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-emerald-500" />
              <span className="text-xs text-muted-foreground">{locale === 'es-MX' ? 'Mejor día' : 'Best day'}</span>
            </div>
            <p className="text-2xl font-bold">{stats.best_day?.points || 0}</p>
            <p className="text-xs text-muted-foreground">{stats.best_day?.date ? formatDate(stats.best_day.date) : '-'}</p>
          </div>
          
          <div className="stat-card" data-testid="stat-worst">
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="w-4 h-4 text-rose-500" />
              <span className="text-xs text-muted-foreground">{locale === 'es-MX' ? 'Peor día' : 'Worst day'}</span>
            </div>
            <p className="text-2xl font-bold">{stats.worst_day?.points || 0}</p>
            <p className="text-xs text-muted-foreground">{stats.worst_day?.date ? formatDate(stats.worst_day.date) : '-'}</p>
          </div>
        </div>
      )}

      {/* Toggle View */}
      <div className="flex items-center gap-3 mb-4">
        <Label className="text-sm">{locale === 'es-MX' ? 'Ver por categoría' : 'View by category'}</Label>
        <Switch
          checked={showByCategory}
          onCheckedChange={setShowByCategory}
          data-testid="toggle-view"
        />
      </div>

      {/* Charts */}
      {!showByCategory ? (
        <Card data-testid="daily-chart">
          <CardHeader>
            <CardTitle className="text-base">{locale === 'es-MX' ? 'Puntos por día' : 'Points by day'}</CardTitle>
          </CardHeader>
          <CardContent className="px-2 md:px-6">
            <div className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10 }}
                    stroke="hsl(var(--muted-foreground))"
                    interval="preserveStartEnd"
                  />
                  <YAxis 
                    tick={{ fontSize: 10 }}
                    stroke="hsl(var(--muted-foreground))"
                    width={30}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    labelFormatter={(value, payload) => payload[0]?.payload?.fullDate ? formatDate(payload[0].payload.fullDate) : value}
                  />
                  <Bar dataKey="points" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card data-testid="category-chart">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              {locale === 'es-MX' ? 'Puntos por categoría' : 'Points by category'}
              <span className="text-xs text-muted-foreground font-normal">
                ({locale === 'es-MX' ? 'Toca una barra para ver detalle' : 'Tap a bar to see details'})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 md:px-6">
            <div style={{ height: Math.max(200, categoryChartData.length * 50) }} className="w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={categoryChartData} 
                  layout="vertical" 
                  margin={{ top: 5, right: 20, left: 5, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis 
                    dataKey="name" 
                    type="category" 
                    tick={{ fontSize: 11 }}
                    stroke="hsl(var(--muted-foreground))"
                    width={80}
                    tickFormatter={(value) => value.length > 12 ? value.substring(0, 12) + '...' : value}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar 
                    dataKey="points" 
                    radius={[0, 4, 4, 0]} 
                    onClick={handleBarClick}
                    style={{ cursor: 'pointer' }}
                  >
                    {categoryChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Drill-down Modal */}
      <Dialog open={!!drillDownCategory} onOpenChange={(open) => !open && closeDrillDown()}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby="drilldown-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <button onClick={closeDrillDown} className="p-1 hover:bg-secondary rounded">
                <ArrowLeft className="w-4 h-4" />
              </button>
              {locale === 'es-MX' ? 'Detalle' : 'Details'}: {drillDownCategory?.name}
            </DialogTitle>
            <p id="drilldown-description" className="sr-only">
              {locale === 'es-MX' ? 'Desglose de puntos por subcategoría y registros recientes' : 'Points breakdown by subcategory and recent logs'}
            </p>
          </DialogHeader>

          {loadingDrillDown ? (
            <div className="text-center py-8 text-muted-foreground">
              <div className="animate-pulse">{t('common.loading')}</div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-secondary/50">
                  <p className="text-xs text-muted-foreground">{locale === 'es-MX' ? 'Total puntos' : 'Total points'}</p>
                  <p className="text-2xl font-bold">{drillDownStats?.total_points || 0}</p>
                </div>
                <div className="p-4 rounded-xl bg-secondary/50">
                  <p className="text-xs text-muted-foreground">Días activos</p>
                  <p className="text-2xl font-bold">{drillDownStats?.days || 0}</p>
                </div>
              </div>

              {/* Subcategory breakdown chart */}
              {subcategoryChartData.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Layers className="w-4 h-4" />
                    Desglose por subcategoría
                  </h4>
                  <div style={{ height: Math.max(150, subcategoryChartData.length * 40) }} className="w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart 
                        data={subcategoryChartData} 
                        layout="vertical" 
                        margin={{ top: 5, right: 20, left: 5, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                        <YAxis 
                          dataKey="name" 
                          type="category" 
                          tick={{ fontSize: 11 }}
                          stroke="hsl(var(--muted-foreground))"
                          width={100}
                          tickFormatter={(value) => value.length > 15 ? value.substring(0, 15) + '...' : value}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                        />
                        <Bar dataKey="points" fill={drillDownCategory?.color || '#6b7280'} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Recent logs list */}
              <div>
                <h4 className="text-sm font-medium mb-3">Registros recientes</h4>
                {drillDownLogs.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No hay registros en este período</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {drillDownLogs.map((log) => (
                      <div 
                        key={log.id} 
                        className="p-3 rounded-xl bg-secondary/50"
                        data-testid={`drilldown-log-${log.id}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-muted-foreground">{formatDate(log.date)}</span>
                          <span className="text-sm font-bold">{log.points} pts</span>
                        </div>
                        {log.subcategory_name && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                            {log.subcategory_name}
                          </span>
                        )}
                        {log.note && <p className="text-sm mt-1 text-muted-foreground">{log.note}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Link to settings */}
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  closeDrillDown()
                  // Navigate to settings - in a real app you'd use react-router
                  window.location.href = '/ajustes'
                }}
              >
                <Settings className="w-4 h-4 mr-2" />
                Administrar subcategorías
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
